
import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { format } from 'date-fns';
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear';
import { TableRow, TableCell } from '@mui/material'

import { BASE_URL, getAxiosConfig } from '../../../components/interface/Config';
import DateRangePicker from '../../../components/FormFilelds/DateRangePicker';
import IconButton from '../../../components/Buttons/SubjectiveIcons';
import ChartButton from '../../../components/Button/ChartButton';
import Chart from '../../../components/Chart'
import TableComponent from '../../../components/Table/TableComponent';
import TablePaginationComponent from '../../../components/TablePaginationComponent';
import { ObjectiveResponse, Temperature } from '../../../components/interface/ObjectiveInterface';


const TemperatureForm: React.FC<{ patientId: string }> = ({ patientId }) => {
  const [temperatures, setTemperatures] = useState<Temperature[]>([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)
  const [openTemperatureDialog, setOpenTemperatureDialog] = useState(false)
  const [editTemperature, setEditTemperature] = useState<Temperature | null>(null)
  const [showChart, setShowChart] = useState(false)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: ''
  })
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    'success' | 'info' | 'warning' | 'error'
  >('success')

  const [filtersApplied, setFiltersApplied] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [recordToDelete, setRecordToDelete] = useState<Temperature | null>(null)
  const showSnackbar = (message: string, severity: 'success' | 'info' | 'warning' | 'error') => {
    setSnackbarMessage(message)
    setSnackbarSeverity(severity)
    setSnackbarOpen(true)
  }

  useEffect(() => {
   
    fetchTemperatures(dateRange.startDate, dateRange.endDate);
  }, [page, rowsPerPage, searchTerm])


  const fetchTemperatures = async (startDate: string = '', endDate: string = '') => {
    setLoading(true);
    try {
      const response = await axios.get<ObjectiveResponse>(`${BASE_URL}/resource/vitals`, {
        params: {
          limit: rowsPerPage,
          page: page + 1,
          from: startDate || '', // Fallback to empty string
          to: endDate || '', // Fallback to empty string
          order_by: 'details->date',
          dir: 2,
          user_id: patientId,
          slug: 'temperature',
          searchkey: searchTerm || undefined,
        },
        headers: getAxiosConfig ().headers
      });

      console.log('Full API response:', response.data);

      const { vitals } = response.data.data;
      const total = response.data.data.pagination!.total;

      console.log('Fetched vitals:', vitals);

      if (Array.isArray(vitals) && vitals.length > 0) {
        const formattedData: Temperature[] = vitals.map((vital) => {
          const details = vital.details; // Get the details object

          return {
            id: vital.id,
            user_id: vital.user_id, // Add user_id
            consult_id: vital.consult_id ?? null, // Add consult_id
            peripheral_id: vital.peripheral_id ?? null, // Add peripheral_id
            slug: 'temperature', // Set slug
            details: {
              date: details?.date, // Access nested date inside details
              time: details?.time, // Access nested time inside details
              type: details?.type, // Access nested type inside details
              unit: details?.unit, // Access nested unit inside details
              range_code: details?.range_code ?? '', // Access range code
              temperature: details?.temperature, // Access nested temperature inside details
              temperatureFlag: details?.temperatureFlag, // Add temperatureFlag
              temperatureFlagColor: details?.temperatureFlagColor, // Add temperatureFlagColor
            },
            freeze: vital.freeze, // Set default freeze value
            document: null, // Set default document value
          };
        });
        setTemperatures(formattedData);
        setTotalRecords(total);

      } else {
        setTemperatures([]); // Reset records if no vitals found
      }
    } catch (error) {
      console.error('Error fetching temperatures:', error);
      setTemperatures([]); // Reset temperatures on error
      setTotalRecords(0); // Reset total records on error
    } finally {
      setLoading(false);
    }
  };


  const handleAddTemperatureClick = () => {
    setEditTemperature(null)
    fetchTemperatures()
    setOpenTemperatureDialog(true)
  }

  const handleDialogClose = () => {
    setOpenTemperatureDialog(false)
    fetchTemperatures()
    fetchTemperatures(dateRange.startDate, dateRange.endDate)
  }

  const handleDialogSave = async (newTemperature: Temperature) => {
    try {
      if (editTemperature) {
        // Update the temperature
        setTemperatures((prev) =>
          prev.map((record) => (record.id === editTemperature.id ? newTemperature : record))
        );
        setSnackbarMessage('Temperature updated successfully!');
      } else {
        // Add a new temperature
        setTemperatures((prev) => [...prev, newTemperature]);
        setSnackbarMessage('Temperature added successfully!');
      }
      setSnackbarSeverity('success');
      setSnackbarOpen(true); // Show the snackbar
    } catch (error) {
      console.error('Error saving temperature:', error);
      setSnackbarMessage('Failed to save temperature.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true); // Show the snackbar for error
    } finally {
      setOpenTemperatureDialog(false);
      fetchTemperatures(); // Fetch updated list
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('')
    setDateRange({ startDate: '', endDate: '' })
    setPage(0) // Reset to the first page
    setRowsPerPage(5) // Reset rows per page if needed
    fetchTemperatures() // Fetch all records again 
    setFiltersApplied(false);// Hide the clear button
  }

  const handleEdit = (temperature: Temperature) => {
    setEditTemperature(temperature)
    setOpenTemperatureDialog(true)
  }

  const handleDelete = (record: Temperature) => {
    setRecordToDelete(record)
    setDeleteDialogOpen(true) // Open delete confirmation dialog
  }



  const cancelDelete = () => {
    setDeleteDialogOpen(false)
    setRecordToDelete(null)
  }

  const formatDate = (dateString: string | number | Date) => {
    return format(new Date(dateString), 'dd-MM-yyyy');
  };

  const renderRow = (item: Temperature, index: number) => {
    // Function to convert Fahrenheit to Celsius
    const fahrenheitToCelsius = (fahrenheit: number) => {
      return ((fahrenheit - 32) * 5) / 9;
    };
  
    return (
      <TableRow key={item.id}>
        <TableCell className="px-4 py-3 font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{index + 1}</TableCell>
        <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{formatDate(item.details.date)}</TableCell> {/* Date of record */}
        <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{item.details.time}</TableCell> {/* Time of record */}
        <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{item.details.type}</TableCell> {/* Type of measurement */}   
        <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>
          {item.details.temperature} °F / {fahrenheitToCelsius(item.details.temperature).toFixed(2)} °C
        </TableCell> {/* Temperature with unit (Fahrenheit and Celsius) */}
        
        <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>
          <span
            style={{
              backgroundColor: item.details.range_code,
              padding: '4px 8px',
              borderRadius: '8px',
              color: '#F9F9F9'
            }}
          >
            {item.details.temperatureFlag}
          </span> {/* Temperature status */}
        </TableCell>
        
      </TableRow>
    );
  }
  const chartData = {
    labels: temperatures.map((record) => record.details.date),
    datasets: [
      {
        label: 'Temperature (°F)',
        data: temperatures.map((record) => record.details.temperature),
        borderColor: 'rgba(255, 99, 132, 1)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        borderWidth: 2
      }
    ]
  }
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.dataset.label}: ${context.raw} bpm`
        }
      }
    }
  }

  return (
    <div>
      <div className="flex items-end justify-between w-full gap-12">
        <div className="flex flex-grow space-x-2 gap-2">
          <DateRangePicker
            startDateId={'Temperature_Start_Date'}
            endDateId={'Temperature_End_Date'}
            onChange={(dates: { startDate: string; endDate: string }) => {
              setDateRange(dates);
              setFiltersApplied(true);
            }}
            value={dateRange} // Pass the selected date range
          />
           <IconButton
                icon={<SearchIcon />}
                onClick={() => {
                  fetchTemperatures(dateRange.startDate, dateRange.endDate);
                  setFiltersApplied(true); // Mark filters as applied when search is clicked
                }}
              />

              {filtersApplied && (
                <IconButton
                  icon={<ClearIcon />}
                  onClick={handleClearFilters}
                />
              )}
          
          </div>
       
        <div className="flex justify-end space-x-4">
          <ChartButton
            text={showChart ? 'List' : 'Chart'}
            onClick={() => setShowChart((prev) => !prev)}
          />
        </div>
      </div>
 
      {showChart ? (
        <Chart data={chartData} options={chartOptions} />
      ) : (
        <>
          <TableComponent
            headers={['S.No', 'Date', 'Time', 'Method', 'Temperature', 'Result' ]}
            data={temperatures}
            renderRow={renderRow} error={''} />
         
        </>
      )}
      <TablePaginationComponent
        count={totalRecords}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_event, newPage) => {
          setPage(newPage);
        }}
        onRowsPerPageChange={(event) => {
          const newRowsPerPage = parseInt(event.target.value, 10);
          setRowsPerPage(newRowsPerPage);
          setPage(0);
        }}
      />
    
    </div>
  )
}

export default TemperatureForm
